/**
 * What search engines and AI assistants read about the public site: each page's title, description and canonical address,
 * the questions answered on the pages (shown there and repeated as FAQPage data), the JSON-LD, robots.txt, sitemap.xml,
 * llms.txt and llms-full.txt. Plain TypeScript with no React or browser APIs, because the server imports it too
 * (apps/server/src/site.ts) to write the <head> of each page and the text files. Keep every claim true to the product.
 * The comparison pages, guides and about page are in articles.ts.
 */
import { ARTICLE_META, ARTICLES, articleMarkdown, CHECKED, COMPARE_PATHS, GUIDE_PATHS, longDate, type ArticleMeta, type ArticlePath } from './articles';

/** What the copy depends on: the address, who sells it and the live plan (from /api/site and /api/billing/plan). */
export interface Facts {
  /** The site's address without a trailing slash, e.g. https://raremargin.com. */
  origin: string;
  business: string;
  email: string;
  plan: { price: number; currency: string; interval: string; intervalCount: number } | null;
  trialDays: number;
  aiMonthlyMessages: number;
  aiTrialMessages: number;
  /** Who sells it and where, for the about page and the Organization data; empty until the server has them. */
  legalName?: string;
  address?: string;
}

/** A question with its answer in plain text. Each of `links` turns the first occurrence of its `text` (after the previous link) into a link. */
export interface Faq {
  q: string;
  a: string;
  links?: { text: string; to: string }[];
}

export type PagePath = '/' | '/pricing' | '/contact' | '/terms' | '/privacy' | '/refunds' | '/dpa' | ArticlePath;

interface Page {
  /** The <title>, about 60 characters at most. */
  title: string;
  /** The meta description, about 70–160 characters. */
  description: (f: Facts) => string;
  /** The page's name in breadcrumbs and llms.txt. */
  name: string;
  type: 'WebPage' | 'ContactPage' | 'AboutPage';
  /** When the page's content last changed (ISO date), for the sitemap and dateModified. */
  updated: string;
  /** For the pages in articles.ts. */
  article?: ArticleMeta;
}

/** The legal pages' "Updated" date (Legal.tsx shows it). */
export const LEGAL_UPDATED = '2026-11-04';
const SITE_UPDATED = '2026-11-03';

export const PAGES: Record<PagePath, Page> = {
  '/': {
    title: 'RareMargin · AI-native general ledger with continuous close',
    description: (f) =>
      `RareMargin: AI-native general ledger for SaaS finance teams. ASC 606 revenue, accruals and bank matching post as they happen. ${offer(f)}.`,
    name: 'Home',
    type: 'WebPage',
    updated: SITE_UPDATED,
  },
  '/pricing': {
    title: 'RareMargin pricing (2026): one plan with every feature',
    description: (f) =>
      `${priceText(f.plan) ? `${cap(priceText(f.plan)!)} per company` : 'One plan per company'} for the general ledger, revenue, AR, AP, cash, close, reports, the AI assistant and the API. ${f.trialDays}-day free trial, no card.`,
    name: 'Pricing',
    type: 'WebPage',
    updated: SITE_UPDATED,
  },
  '/contact': {
    title: 'Contact RareMargin: support, billing and security',
    description: (f) => `Write to RareMargin at ${f.email} for support, billing or security reports. A person replies within two business days.`,
    name: 'Contact',
    type: 'ContactPage',
    updated: SITE_UPDATED,
  },
  '/terms': {
    title: 'Terms of service · RareMargin',
    description: () => 'The terms for using RareMargin: accounts, the free trial, subscriptions sold through Dodo Payments, your data, acceptable use and liability.',
    name: 'Terms of service',
    type: 'WebPage',
    updated: LEGAL_UPDATED,
  },
  '/privacy': {
    title: 'Privacy policy · RareMargin',
    description: () => 'What RareMargin collects and why, who processes it, how long it is kept, the Reddit ad pixel and your choices, and how to use your privacy rights.',
    name: 'Privacy policy',
    type: 'WebPage',
    updated: LEGAL_UPDATED,
  },
  '/refunds': {
    title: 'Refund and cancellation policy · RareMargin',
    description: () => 'How to cancel a RareMargin subscription, when you get a refund, including a full refund within 14 days of your first payment, and how to ask for one.',
    name: 'Refund policy',
    type: 'WebPage',
    updated: LEGAL_UPDATED,
  },
  '/dpa': {
    title: 'Data processing terms · RareMargin',
    description: () => 'How RareMargin processes the books you keep in it on your behalf: your instructions, subprocessors, security, transfers, help with requests, deletion.',
    name: 'Data processing terms',
    type: 'WebPage',
    updated: LEGAL_UPDATED,
  },
  ...articlePages(),
};

/** The pages in articles.ts, in the order the sitemap and llms.txt list them. */
function articlePages() {
  const pages = {} as Record<ArticlePath, Page>;
  for (const path of [...COMPARE_PATHS, ...GUIDE_PATHS, '/about'] as const) {
    const meta = ARTICLE_META[path];
    pages[path] = {
      title: meta.title,
      description: (f) => meta.description(f, priceText(f.plan)),
      name: meta.name,
      type: meta.kind === 'about' ? 'AboutPage' : 'WebPage',
      updated: meta.updated,
      article: meta,
    };
  }
  return pages;
}

/** An article's words for these facts (Article.tsx renders them). */
export const article = (path: ArticlePath, f: Facts) => ARTICLES[path](f, priceText(f.plan));
export const isArticle = (path: PagePath): path is ArticlePath => !!PAGES[path].article;

export const isPage = (path: string): path is PagePath => Object.hasOwn(PAGES, path);

/** "$99 a month", "$990 a year", "$30 every 3 months"; null while there is no plan. */
export function priceText(plan: Facts['plan']) {
  if (!plan) return null;
  const major = plan.price / 100;
  const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: plan.currency, minimumFractionDigits: Number.isInteger(major) ? 0 : 2 }).format(major);
  const unit = plan.interval.toLowerCase();
  return plan.intervalCount > 1 ? `${amount} every ${plan.intervalCount} ${unit}s` : `${amount} a ${unit}`;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const offer = (f: Facts) => (priceText(f.plan) ? `${cap(priceText(f.plan)!)}, ${f.trialDays}-day free trial` : `Free for ${f.trialDays} days`);

/** One sentence on what RareMargin is, used as the lead everywhere an engine might quote one. */
export const definition = 'RareMargin is an AI-native general ledger with continuous close for finance teams at SaaS companies.';

/** The questions on the home page. */
export function homeFaq(f: Facts): Faq[] {
  const price = priceText(f.plan);
  return [
    {
      q: 'What is RareMargin?',
      a: `${definition} Revenue schedules, amortization, accruals and bank matching post to one double-entry ledger as they happen, AI drafts the entries you would write by hand, and nothing reaches the books until a person approves it.`,
    },
    {
      q: 'What is continuous close?',
      a: 'Continuous close keeps the books closed through the month instead of catching up at the end of it. In RareMargin, recognized revenue and amortization post on schedule, accruals are suggested when a regular bill hasn’t arrived, bank lines are matched as they come in, and controls such as the trial balance and tie-outs are checked continuously. By month-end the postings are done, and the close is a review and sign-off.',
    },
    {
      q: 'Who is RareMargin for?',
      a: 'RareMargin is for controllers, accountants and finance leads at SaaS companies who recognize subscription revenue under ASC 606, run more than one entity or currency, and want month-end to be a review rather than a scramble. It is priced per company with no per-user fee, so the whole finance team and the people who approve its entries can work in it.',
    },
    {
      q: 'How much does RareMargin cost?',
      a: price
        ? `RareMargin costs ${price} per company, with every feature included: the ledger, revenue and AR, spend and AP, cash, the close, 13 reports, the AI assistant and the API. Every company starts with a ${f.trialDays}-day free trial, and no card is needed to start.`
        : `Every company starts with a ${f.trialDays}-day free trial with every feature, and no card is needed to start. The subscription price is on the pricing page.`,
      links: [{ text: 'pricing page', to: '/pricing' }],
    },
    {
      q: 'Does the AI post entries to the ledger by itself?',
      a: 'No. RareMargin’s assistant and any MCP client can only propose an entry. Each proposal goes to the approval queue with its reasoning, and it posts only after a person approves it. Approval rules also enforce segregation of duties, so nobody approves their own entry.',
      links: [{ text: 'approval queue', to: '/docs/ai-journal-entries-and-approvals' }],
    },
    {
      q: 'Does RareMargin handle ASC 606 revenue recognition?',
      a: 'Yes. RareMargin turns each customer contract into a revenue schedule that posts recognized revenue as each service period arrives, deferred revenue rolls forward on its own, and MRR, ARR, NRR and GRR are read from the same contracts.',
      links: [{ text: 'revenue schedule', to: '/docs/asc-606-revenue-recognition' }],
    },
    {
      q: 'Can I connect Claude or another AI agent to my books?',
      a: 'Yes. RareMargin runs a Model Context Protocol (MCP) server at /mcp with the same tools as the built-in assistant, and Claude (on the web, Desktop and Code) and ChatGPT connect to it by signing in to RareMargin; Cursor and Codex use an API key. Write tools create approval requests, never postings. A versioned REST API at /v1 and signed webhooks cover scripts and integrations.',
      links: [
        { text: 'Model Context Protocol', to: 'https://modelcontextprotocol.io/' },
        { text: 'Claude (on the web, Desktop and Code) and ChatGPT', to: '/docs/use-raremargin-in-claude-chatgpt-and-cursor' },
        { text: 'REST API', to: '/docs/api-mcp-server-and-webhooks' },
      ],
    },
    {
      q: 'How does RareMargin compare with Rillet, QuickBooks Online and NetSuite?',
      a: `RareMargin is self-serve${price ? ` at ${price} per company` : ''}, with a published price. Rillet is sold through a demo, with a managed implementation and SOC 2 reports; QuickBooks Online suits small businesses; NetSuite is a full ERP on an annual license.`,
      links: [
        { text: 'Rillet', to: '/compare/raremargin-vs-rillet' },
        { text: 'QuickBooks Online', to: '/compare/raremargin-vs-quickbooks-online' },
        { text: 'NetSuite', to: '/compare/raremargin-vs-netsuite' },
      ],
    },
    {
      q: 'Can I try RareMargin without my own data?',
      a: `Yes. RareMargin can start you on a sample SaaS company with 14 months of history: contracts, invoices, bills, bank lines and a closed ledger to explore for ${f.trialDays} days.`,
    },
  ];
}

/** The questions on /pricing. */
export function pricingFaq(f: Facts): Faq[] {
  const price = priceText(f.plan);
  return [
    { q: 'Do I need a card to start the trial?', a: 'No. RareMargin’s trial needs no card: sign up, use everything, and add a card only when you subscribe.' },
    {
      q: 'Is RareMargin priced per user?',
      a: `No. RareMargin is priced per company${price ? `, at ${price}` : ''}, with no per-user fee: invite as many people as the company needs, from viewers to the people who approve entries.`,
    },
    {
      q: 'How does RareMargin’s price compare with Rillet, QuickBooks Online and NetSuite?',
      // Competitor prices from the comparison pages, with the date they were read: re-read them when CHECKED changes.
      a: `RareMargin publishes one price${price ? `, ${price} per company` : ''}. QuickBooks Online lists $38 to $340 a month, depending on the plan and the number of users (Intuit’s US list prices on ${longDate(CHECKED)}). Rillet and NetSuite don’t publish prices; both quote through sales.`,
      links: [
        { text: 'QuickBooks Online', to: '/compare/raremargin-vs-quickbooks-online' },
        { text: 'Rillet', to: '/compare/raremargin-vs-rillet' },
        { text: 'NetSuite', to: '/compare/raremargin-vs-netsuite' },
      ],
    },
    {
      q: 'How long does it take to set up RareMargin?',
      a: 'You can sign up and use RareMargin the same day, on your own books or on a sample company. Moving your books in takes longer, because there is no one-click import yet; the setup guide walks through each step.',
      links: [{ text: 'setup guide', to: '/docs/set-up-raremargin-and-move-your-books' }],
    },
    {
      q: 'What counts as an assistant message?',
      a: `Each question you send RareMargin’s assistant is one message, whatever tools it uses to answer. A subscribed company gets ${f.aiMonthlyMessages.toLocaleString('en-US')} a month, counted per calendar month in UTC; a trial gets ${f.aiTrialMessages}.`,
    },
    {
      q: 'What happens when the trial ends?',
      a: 'If nobody has subscribed, the company becomes read-only in RareMargin: nothing is deleted, reports and CSV downloads keep working, and subscribing turns writing back on straight away.',
    },
    { q: 'Can I cancel?', a: 'Yes, any time, from the billing portal in RareMargin’s Settings → Billing. You keep full access until the end of the period you have paid for.' },
    { q: 'Who handles the payment?', a: 'Dodo Payments, RareMargin’s merchant of record. They take the payment, send the invoice and collect sales tax or VAT where it is due.' },
    {
      q: 'Do you give refunds?',
      a: 'Yes. RareMargin gives a full refund within 14 days of your first payment; the refund policy has the details. If you were charged by mistake, write to us and we will sort it out.',
      links: [{ text: 'refund policy', to: '/refunds' }],
    },
    { q: 'Can I get my data out?', a: 'Yes. Every RareMargin report downloads as CSV, and the REST API reads every account, entry, contract, invoice and bill.' },
  ];
}

const FEATURES = [
  'Double-entry general ledger, multi-entity and multi-currency',
  'ASC 606 revenue schedules from contracts, with deferred revenue',
  'Invoices, collections and AR aging',
  'Bills, vendors, AP aging, suggested accruals and prepaid amortization',
  'Bank matching with explained scores, and a 13-week cash forecast',
  'Continuous controls and a close checklist',
  'SHA-256 hash chain over every posted entry',
  'Approval rules with materiality tiers and segregation of duties',
  '13 reports, from the trial balance to SaaS metrics (MRR, ARR, NRR, GRR)',
  'AI assistant that drafts entries for approval',
  'REST API, MCP server and signed webhooks',
];

// --- <head> ---------------------------------------------------------------------------------------------------------

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/** JSON for inside a <script> element: `<` escaped, so the text can't close the element. */
export const scriptJson = (value: unknown) => JSON.stringify(value).replace(/</g, '\\u003c');

export const OG_IMAGE = { path: '/og.jpg', width: 1200, height: 630, alt: 'RareMargin: the general ledger that closes as you go. A split-flap board reads Books Closed in a meadow.' };

/** Everything a page's <head> needs besides the charset, viewport and the app's own assets. */
export function headTags(path: PagePath, f: Facts) {
  const page = PAGES[path];
  const url = f.origin + path;
  const description = page.description(f);
  const image = f.origin + OG_IMAGE.path;
  return [
    `<title>${esc(page.title)}</title>`,
    `<meta name="description" content="${esc(description)}" />`,
    `<link rel="canonical" href="${esc(url)}" />`,
    `<meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${esc(f.business)}" />`,
    `<meta property="og:locale" content="en_US" />`,
    `<meta property="og:url" content="${esc(url)}" />`,
    `<meta property="og:title" content="${esc(page.title)}" />`,
    `<meta property="og:description" content="${esc(description)}" />`,
    `<meta property="og:image" content="${esc(image)}" />`,
    `<meta property="og:image:width" content="${OG_IMAGE.width}" />`,
    `<meta property="og:image:height" content="${OG_IMAGE.height}" />`,
    `<meta property="og:image:alt" content="${esc(OG_IMAGE.alt)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${esc(page.title)}" />`,
    `<meta name="twitter:description" content="${esc(description)}" />`,
    `<meta name="twitter:image" content="${esc(image)}" />`,
    `<meta name="twitter:image:alt" content="${esc(OG_IMAGE.alt)}" />`,
    `<meta name="theme-color" content="#123d33" />`,
    `<link rel="apple-touch-icon" href="/apple-touch-icon.png" />`,
    `<link rel="alternate" type="text/markdown" title="${esc(f.business)} for AI assistants" href="/llms-full.txt" />`,
    // A data block, which browsers never run, so the CSP's script-src doesn't apply to it.
    `<script type="application/ld+json">${scriptJson(jsonLd(path, f))}</script>`,
  ].join('\n    ');
}

/** Schema.org data for a page, as one @graph: who we are, the site, the page, and what the page is about. */
export function jsonLd(path: PagePath, f: Facts) {
  const page = PAGES[path];
  const url = f.origin + path;
  const org = { '@id': `${f.origin}/#organization` };
  const app = { '@id': `${f.origin}/#software` };
  // "Rare Margin", the way people (and search engines that split the name) write it.
  const spaced = f.business.replace(/([a-z])([A-Z])/g, '$1 $2');
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Organization',
      ...org,
      name: f.business,
      ...(spaced !== f.business ? { alternateName: spaced } : {}),
      ...(f.legalName ? { legalName: f.legalName } : {}),
      ...(f.address ? { address: f.address } : {}),
      url: `${f.origin}/`,
      logo: { '@type': 'ImageObject', url: `${f.origin}/icon-512.png`, width: 512, height: 512 },
      email: f.email,
      description: definition,
      contactPoint: { '@type': 'ContactPoint', contactType: 'customer support', email: f.email, availableLanguage: 'English', url: `${f.origin}/contact` },
    },
    { '@type': 'WebSite', '@id': `${f.origin}/#website`, url: `${f.origin}/`, name: f.business, description: definition, publisher: org, inLanguage: 'en' },
    {
      '@type': page.type,
      '@id': `${url}#webpage`,
      url,
      name: page.title,
      description: page.description(f),
      isPartOf: { '@id': `${f.origin}/#website` },
      inLanguage: 'en',
      dateModified: page.updated,
      ...(path === '/' || path === '/pricing' ? { about: app, primaryImageOfPage: { '@type': 'ImageObject', url: f.origin + OG_IMAGE.path, width: OG_IMAGE.width, height: OG_IMAGE.height } } : {}),
      ...(path === '/' ? {} : { breadcrumb: { '@id': `${url}#breadcrumb` } }),
      ...(page.type === 'AboutPage' ? { about: org, mainEntity: org } : {}),
    },
  ];
  if (path !== '/') {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: f.business, item: `${f.origin}/` },
        { '@type': 'ListItem', position: 2, name: page.name, item: url },
      ],
    });
  }
  const meta = page.article;
  if (meta && meta.kind !== 'about') {
    graph.push({
      '@type': meta.kind === 'guide' ? 'TechArticle' : 'Article',
      '@id': `${url}#article`,
      headline: page.title,
      description: page.description(f),
      url,
      mainEntityOfPage: { '@id': `${url}#webpage` },
      datePublished: meta.published,
      dateModified: meta.updated,
      author: org,
      publisher: org,
      about: app,
      inLanguage: 'en',
      ...(meta.mentions ? { mentions: meta.mentions.map((m) => ({ '@type': 'SoftwareApplication', name: m.name, url: m.url })) } : {}),
    });
  }
  if (path === '/' || path === '/pricing') graph.push(software(f));
  const faq = path === '/' ? homeFaq(f) : path === '/pricing' ? pricingFaq(f) : null;
  if (faq) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      url,
      mainEntity: faq.map((x) => ({ '@type': 'Question', name: x.q, acceptedAnswer: { '@type': 'Answer', text: x.a } })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

function software(f: Facts) {
  const plan = f.plan;
  return {
    '@type': 'SoftwareApplication',
    '@id': `${f.origin}/#software`,
    name: f.business,
    url: `${f.origin}/`,
    description: `${definition} Revenue, amortization and accruals post the day they happen, AI drafts entries, and a person approves every one before it reaches the books.`,
    applicationCategory: 'FinanceApplication',
    applicationSubCategory: 'General ledger and accounting',
    operatingSystem: 'Web browser',
    browserRequirements: 'A current version of Chrome, Edge, Firefox or Safari',
    image: f.origin + OG_IMAGE.path,
    featureList: FEATURES,
    publisher: { '@id': `${f.origin}/#organization` },
    audience: { '@type': 'BusinessAudience', audienceType: 'Finance teams at SaaS companies' },
    ...(plan
      ? {
          offers: {
            '@type': 'Offer',
            url: `${f.origin}/pricing`,
            price: (plan.price / 100).toFixed(2),
            priceCurrency: plan.currency,
            availability: 'https://schema.org/InStock',
            description: `Per company, every feature included, after a ${f.trialDays}-day free trial with no card.`,
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: (plan.price / 100).toFixed(2),
              priceCurrency: plan.currency,
              referenceQuantity: { '@type': 'QuantitativeValue', value: plan.intervalCount, unitCode: UNIT_CODE[plan.interval] ?? 'MON' },
            },
            seller: { '@id': `${f.origin}/#organization` },
          },
        }
      : {}),
  };
}

/** UN/CEFACT codes for the billing interval. */
const UNIT_CODE: Record<string, string> = { Day: 'DAY', Week: 'WEE', Month: 'MON', Year: 'ANN' };

// --- Text files -----------------------------------------------------------------------------------------------------

/** Every search engine and AI assistant may read the public pages; nobody needs the API, the MCP server or the app. */
export function robotsTxt(f: Facts) {
  const rules = ['Allow: /', 'Disallow: /api/', 'Disallow: /v1/', 'Disallow: /mcp'];
  // Named so it's plain that AI search and assistants are welcome; a crawler follows only the most specific group, so
  // each group carries the same rules.
  const bots = [
    'Googlebot',
    'Google-Extended',
    'Bingbot',
    'Applebot',
    'Applebot-Extended',
    'DuckDuckBot',
    'DuckAssistBot',
    'OAI-SearchBot',
    'ChatGPT-User',
    'GPTBot',
    'Claude-SearchBot',
    'Claude-User',
    'ClaudeBot',
    'PerplexityBot',
    'Perplexity-User',
    'Amazonbot',
    'meta-externalagent',
    'MistralAI-User',
    'CCBot',
  ];
  return [
    `# ${f.business} (${f.origin}): search engines and AI assistants are welcome to read and cite the public site.`,
    '# The app, the API (/v1) and the MCP server (/mcp) need an account and have nothing to index.',
    '',
    ...bots.map((b) => `User-agent: ${b}`),
    ...rules,
    '',
    'User-agent: *',
    ...rules,
    '',
    `Sitemap: ${f.origin}/sitemap.xml`,
    '',
  ].join('\n');
}

export function sitemapXml(f: Facts) {
  const urls = (Object.keys(PAGES) as PagePath[]).map((p) => `  <url>\n    <loc>${esc(f.origin + p)}</loc>\n    <lastmod>${PAGES[p].updated}</lastmod>\n  </url>`);
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls.join('\n')}\n</urlset>\n`;
}

/** The llms.txt proposal (llmstxt.org): a short Markdown map of the site for AI assistants. */
export function llmsTxt(f: Facts) {
  const price = priceText(f.plan);
  const page = (p: PagePath, note: string) => `- [${PAGES[p].name}](${f.origin}${p}): ${note}`;
  return `# ${f.business}

> ${definition} Revenue schedules, amortization, accruals and bank matching post to one double-entry ledger as they happen; AI drafts entries and a person approves every one before it posts. ${price ? `${cap(price)} per company, every feature included` : 'One plan per company, every feature included'}, with a ${f.trialDays}-day free trial and no card needed.

Key facts:
- Product: general ledger and continuous close (ASC 606 revenue, AR, AP, cash, close, controls, reports), with an AI assistant, a REST API, an MCP server and webhooks.
- For: controllers, accountants and finance leads at SaaS companies.
- Price: ${price ? `${price} per company, with no per-user fee` : 'shown on the pricing page'}; ${f.trialDays}-day free trial; payments through Dodo Payments, the merchant of record.
- AI safeguard: the assistant and MCP clients can only propose entries; a person approves each before it posts.
- Sign up: ${f.origin}/signup (a sample SaaS company with 14 months of history is available).
- Contact: ${f.email}

## Pages

${page('/', 'what RareMargin does, the ledger, the assistant, the API and common questions')}
${page('/pricing', 'the plan, what is included, how the trial works, and billing questions')}
${page('/contact', 'support, billing and security contacts')}
${page('/about', 'who makes RareMargin, where it runs, how it keeps the books trustworthy, and what it does not do yet')}

## Comparisons

${page('/compare/raremargin-vs-rillet', 'price, setup, AI approvals, integrations, SOC reports and SSO, from each company’s website')}
${page('/compare/raremargin-vs-quickbooks-online', 'list prices, user limits, ASC 606, multiple entities and bank feeds')}
${page('/compare/raremargin-vs-netsuite', 'licensing and setup, ASC 606 revenue models, and scope')}

## Guides

${page('/docs/asc-606-revenue-recognition', 'contracts, SSP allocation, recognition patterns, billing, deferred revenue and terminations')}
${page('/docs/how-continuous-close-works', 'what posts by itself, the 11 controls, the close checklist, accruals, flux and closed periods')}
${page('/docs/ai-journal-entries-and-approvals', 'what the assistant and MCP clients may do, approval rules and segregation of duties')}
${page('/docs/api-mcp-server-and-webhooks', 'REST endpoints, conventions, connecting an MCP client, webhook events and signatures')}
${page('/docs/use-raremargin-in-claude-chatgpt-and-cursor', 'which AI assistants connect, by signing in or with an API key, and setup for claude.ai, ChatGPT, Claude Code, Claude Desktop, Cursor and Codex')}
${page('/docs/set-up-raremargin-and-move-your-books', 'how long setup takes, moving your books from QuickBooks or spreadsheets, the bank CSV format, roles and subscribing')}

## Details

- [Full description for AI assistants](${f.origin}/llms-full.txt): every public page except the legal ones, as Markdown, with all the answers

## Legal

${page('/terms', 'terms of service')}
${page('/privacy', 'privacy policy, processors, cookies and the ad pixel')}
${page('/refunds', 'refund and cancellation policy')}
${page('/dpa', 'data processing terms for the books customers keep in RareMargin')}
`;
}

/**
 * IndexNow (indexnow.org): the key Bing and the other IndexNow engines fetch from `/<key>.txt` to check that a ping to
 * recrawl pages (scripts/indexnow.ts) comes from this site. Public by design.
 */
export const INDEXNOW_KEY = '1804f8fdd326443c78229ad8408caa77';

/** What a page answers to a request that prefers text/markdown: the home page gives llms-full.txt; null for the rest. */
export function pageMarkdown(path: PagePath, f: Facts) {
  if (path === '/') return llmsFullTxt(f);
  return isArticle(path) ? articleMarkdown(path, f, priceText(f.plan)) : null;
}

/** The whole public story in Markdown: llms-full.txt, and what / answers to a request that prefers text/markdown. */
export function llmsFullTxt(f: Facts) {
  const price = priceText(f.plan);
  const qa = (list: Faq[]) => list.map((x) => `### ${x.q}\n\n${x.a}`).join('\n\n');
  return `# ${f.business}

> ${definition}

${f.business} is a general ledger where revenue, amortization and accruals post the day they happen. AI drafts the entries you would write by hand, and nothing reaches the books until a person approves it. Month-end becomes a review, not a scramble.

- Website: ${f.origin}/
- Pricing: ${f.origin}/pricing (${price ? `${price} per company` : 'one plan per company'}, ${f.trialDays}-day free trial, no card needed)
- Start a trial: ${f.origin}/signup
- Contact: ${f.email}

## Built into the ledger

- **ASC 606 revenue schedules.** Each contract becomes a schedule that posts its revenue on time.
- **Double entry, always balanced.** An entry that does not balance never reaches the ledger.
- **SHA-256 hash chain.** Every posting is chained to the one before, so an edit cannot hide.
- **Approvals and segregation of duties.** Rules route entries to a reviewer, and nobody approves their own.
- **Multi-entity and FX.** Subsidiaries, currencies and exchange rates in one set of books.
- **REST API and MCP.** A versioned API, webhooks and an MCP server for AI agents.

## The command center

Everything waiting on a person, on one screen: entries to approve, matches to confirm and accruals the assistant proposed, beside cash, ARR, runway and the controls that check the books.

## Every subledger posts to one ledger

Billing, spend and cash don't sync into the books overnight. They are the books, so every report reads the same posted entries.

### Revenue: contracts in, recognized revenue out

- **Schedules from contracts.** ASC 606 schedules post revenue as each service period arrives; deferred revenue rolls forward on its own.
- **Invoices and collections.** AR aging by customer, and bank receipts matched to invoices.
- **SaaS metrics from the ledger.** MRR waterfall, ARR, NRR and GRR, from the same contracts that drive revenue.

### Spend: bills in, accruals before you ask

- **Bills and vendors.** AP aging by vendor, and spend by vendor, department or account.
- **Accruals, suggested.** When a regular vendor's bill hasn't arrived, the accrual is suggested for you, and it reverses itself next month.
- **Prepaids that amortize.** Amortization posts monthly from the schedule, not from a spreadsheet.

### Cash and close: cash matched, controls always on

- **Bank matching you can explain.** Every suggestion shows its score and the factors behind it.
- **Controls that never sleep.** Trial balance, tie-outs, closed periods, suspense and the hash chain, checked continuously.
- **A close that is a checklist.** By month-end the postings are done; what is left is review and sign-off.

## The assistant: AI drafts the entries, people approve them

Ask about the books, or have it draft the entry. Whatever it proposes goes to the approval queue, and only a person can post it. Each question counts toward the company's monthly assistant allowance (${f.aiMonthlyMessages.toLocaleString('en-US')} messages a month on a subscription, ${f.aiTrialMessages} during the trial).

## By the numbers

- **0** entries the assistant or an MCP client can post without a person approving them.
- **13** reports read straight from the ledger, from the trial balance to a 13-week cash forecast.
- **SHA-256** seal on every posted entry, chained so a change to any one of them shows.
- **${f.trialDays}** days to try all of it, on your books or on a sample company with 14 months of history.

## Developers

The app, the REST API, webhooks and the MCP server all run on the same services, so a script gets exactly the rules a person does.

- **REST API at /v1.** Decimal-string amounts, idempotent writes, signed cursors and RFC 9457 errors. API keys carry a role, and their writes go through the same approval rules as anyone's.
- **MCP server at /mcp.** Connect Claude or any MCP client to the same tools the assistant uses. Write tools create approval requests, never postings.
- **Webhooks.** Signed with HMAC-SHA256, sent from a transactional outbox, retried with backoff, and every delivery logged in Settings.

## Pricing

One plan, every feature: ${price ? `${price} per company` : 'the price is shown on the pricing page and at checkout'}. No modules to unlock, no per-user fee and no add-ons for the assistant or the API. Taxes are added at checkout where they apply.

Included: ${FEATURES.join('; ')}.

How the trial works:
1. **Sign up.** The company gets ${f.trialDays} days of full access and ${f.aiTrialMessages} assistant messages. Start from your own books or a sample company.
2. **Subscribe.** An owner or admin subscribes from Settings → Billing. Days left on the trial carry over, so nothing is charged until it ends.
3. **Or don't.** When a trial ends without a subscription, the company becomes read-only. Nothing is deleted, and everyone can still read and export the books.

## Questions

${qa(homeFaq(f))}

${qa(pricingFaq(f))}

${[...COMPARE_PATHS, ...GUIDE_PATHS, '/about' as const].map((p) => articleMarkdown(p, f, price).replace(/^(#+) /gm, '#$1 ')).join('\n\n')}

## Legal

- Terms of service: ${f.origin}/terms
- Privacy policy: ${f.origin}/privacy
- Refund and cancellation policy: ${f.origin}/refunds
- Data processing terms: ${f.origin}/dpa
`;
}
